import { X } from 'lucide-react';
import type { SelectedScreenMeta } from '@/features/campaigns/types/campaign';
import { MAX_SCREEN_SELECTION } from '@/features/campaigns/constants/campaignOptions';

type SelectedScreenChipsProps = {
  selectedMeta: Record<string, SelectedScreenMeta>;
  onSelect: (screen: Record<string, unknown>) => void;
  onClearAll?: () => void;
};

export function SelectedScreenChips({
  selectedMeta,
  onSelect,
  onClearAll,
}: SelectedScreenChipsProps) {
  const selected = Object.values(selectedMeta);
  if (selected.length === 0) return null;

  const progress = Math.min(1, selected.length / MAX_SCREEN_SELECTION);

  return (
    <div
      className="rounded-[16px] border border-black/[0.06] bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(16,38,73,0.04)]"
      role="region"
      aria-label="Selected screens"
    >
      <div className="flex items-center gap-3">
        <p className="m-0 shrink-0 text-[12.5px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
          {selected.length} of {MAX_SCREEN_SELECTION} selected
        </p>

        <div
          className="hidden h-1.5 max-w-[140px] flex-1 overflow-hidden rounded-full bg-[#EEF1F5] sm:block"
          role="progressbar"
          aria-valuenow={selected.length}
          aria-valuemin={0}
          aria-valuemax={MAX_SCREEN_SELECTION}
        >
          <div
            className="h-full rounded-full bg-[var(--dash-brand)] transition-[width] duration-200 ease-out"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        {onClearAll ? (
          <button
            type="button"
            onClick={onClearAll}
            className="ml-auto shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold text-[var(--dash-muted)] transition hover:bg-rose-50 hover:text-rose-600"
          >
            Clear all
          </button>
        ) : null}
      </div>

      <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
        {selected.map((screen) => (
          <div
            key={screen.id}
            className="inline-flex max-w-[220px] shrink-0 items-center gap-1 rounded-full border border-black/[0.06] bg-[#F7F8FA] py-1 pr-1 pl-2.5 text-[11.5px] text-[var(--dash-ink)]"
          >
            <span className="truncate font-semibold" title={screen.screenName}>
              {screen.screenName}
            </span>
            <button
              type="button"
              onClick={() =>
                onSelect({
                  _id: screen.id,
                  screenName: screen.screenName,
                } as Record<string, unknown>)
              }
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[var(--dash-muted)] transition hover:bg-white hover:text-[var(--dash-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--dash-brand)]"
              title={`Remove ${screen.screenName}`}
              aria-label={`Remove ${screen.screenName}`}
            >
              <X className="h-3 w-3" strokeWidth={2.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
